/**
 * useTaskActivityAttachments - Agregação de anexos de uma tarefa
 *
 * Lista TODOS os anexos vinculados à tarefa — sejam anexos diretos,
 * anexos de andamentos (task_comments) ou anexos de respostas
 * (task_comment_replies). Usado pela aba "Anexos" do modal de tarefas.
 * Paginação server-side via COUNT(*) OVER() na RPC, filtro opcional
 * por tipo de arquivo.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-07 v1.0.0 - Versão inicial. Espelha
 *            useDealActivityAttachments trocando deal → task.
 */
import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('TaskActivityAttachments');

/** Origem do anexo dentro da tarefa */
export type TaskAttachmentSourceType = 'task' | 'comment' | 'reply';

/** Item retornado pela RPC */
export interface TaskActivityAttachment {
  id: string;
  source_type: TaskAttachmentSourceType;
  source_id: string;
  source_title: string;
  source_created_at: string;
  file_name: string;
  file_type: string;
  mime_type: string | null;
  file_size: number | null;
  storage_path: string;
  public_url: string;
  thumbnail_url: string | null;
  uploaded_by: string | null;
  uploaded_by_name: string | null;
  uploaded_by_avatar: string | null;
  created_at: string;
}

/** Filtro por tipo (mesmos valores do enum file_type) */
export type TaskAttachmentTypeFilter =
  | 'all'
  | 'image'
  | 'pdf'
  | 'document'
  | 'spreadsheet'
  | 'other';

interface Options {
  /** Habilita a query (padrão true) — usado para lazy loading da aba */
  enabled?: boolean;
  /** Tamanho da página (padrão 60) */
  pageSize?: number;
}

/**
 * Hook principal — retorna anexos paginados + contagem total
 */
export function useTaskActivityAttachments(
  taskId: string | null | undefined,
  typeFilter: TaskAttachmentTypeFilter = 'all',
  options?: Options
) {
  const { currentTenant } = useAuth();
  const pageSize = options?.pageSize ?? 60;
  const isEnabled =
    (options?.enabled ?? true) && !!taskId && !!currentTenant?.id;

  const query = useInfiniteQuery({
    queryKey: ['task-activity-attachments', taskId, typeFilter],
    enabled: isEnabled,
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      if (!taskId || !currentTenant?.id) {
        return { rows: [] as TaskActivityAttachment[], total: 0 };
      }

      const { data, error } = await supabase.rpc(
        'get_task_activity_attachments',
        {
          p_task_id: taskId,
          p_limit: pageSize,
          p_offset: pageParam,
          p_type_filter: typeFilter === 'all' ? null : typeFilter,
          p_tenant_hint: currentTenant.id,
        }
      );

      if (error) {
        logger.error('Erro ao buscar anexos da tarefa:', error.message);
        throw error;
      }

      const rows = (data ?? []) as (TaskActivityAttachment & {
        total_count: number;
      })[];
      const total = rows[0]?.total_count ? Number(rows[0].total_count) : 0;

      const cleaned: TaskActivityAttachment[] = rows.map(
        ({ total_count: _t, ...rest }) => rest
      );

      return { rows: cleaned, total, offset: pageParam };
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.rows.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    staleTime: 30 * 1000,
  });

  const attachments = useMemo(
    () => query.data?.pages.flatMap((p) => p.rows) ?? [],
    [query.data]
  );

  const totalCount = query.data?.pages[0]?.total ?? 0;

  return {
    attachments,
    totalCount,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
    error: query.error,
  };
}
